/* eslint-disable react/prop-types */
import React from "react";
import { Box } from "@mui/material";
import {
  MDXEditor,
  toolbarPlugin,
  listsPlugin,
  headingsPlugin,
  thematicBreakPlugin,
  quotePlugin,
  linkPlugin,
  BoldItalicUnderlineToggles,
  BlockTypeSelect,
  CreateLink,
  ListsToggle,
  InsertThematicBreak,
  linkDialogPlugin,
} from "@mdxeditor/editor";
import { useDebouncedCallback } from "./useDebouncedCallback";

import "@mdxeditor/editor/style.css";

interface SimpleDebouncedMDXEditorProps {
  value: string;
  onChange: (value: string) => void;
  debounce?: number;
  placeholder?: string;
  readOnly?: boolean;
  minHeight?: string;
}

export const SimpleDebouncedMDXEditor: React.FC<SimpleDebouncedMDXEditorProps> =
  ({
    value,
    onChange,
    debounce = 500,
    placeholder = "Enter description...",
    readOnly = false,
    minHeight = "120px",
  }) => {
    const debouncedOnChange = useDebouncedCallback(onChange, debounce);

    if (readOnly) {
      return (
        <Box
          sx={{
            width: "100%",
            minHeight,
            padding: 2,
            backgroundColor: "rgba(255, 255, 255, 0.05)",
            borderRadius: 1,
            whiteSpace: "pre-wrap",
            "& p": { margin: "8px 0" },
            "& h1, & h2, & h3, & h4, & h5, & h6": { margin: "12px 0 8px 0" },
            "& ul, & ol": { margin: "8px 0", paddingLeft: "20px" },
            "& li": { margin: "4px 0" },
            "& .mdxeditor": { width: "100%" },
          }}
        >
          {value ? (
            <MDXEditor
              className="dark-theme dark-editor"
              markdown={value}
              readOnly={true}
              plugins={[
                headingsPlugin(),
                listsPlugin(),
                thematicBreakPlugin(),
                quotePlugin(),
                linkPlugin(),
              ]}
            />
          ) : (
            "No description provided"
          )}
        </Box>
      );
    }

    return (
      <Box
        sx={{
          width: "100%",
          bgcolor: "#1c1c1c",
          borderRadius: 1,
          border: "1px solid rgba(255, 255, 255, 0.23)",
          minHeight,
          "& .mdxeditor": {
            width: "100%",
            minHeight,
          },
        }}
      >
        <MDXEditor
          key="stable-mdx-editor" // Use a stable key
          className="dark-theme dark-editor"
          markdown={value}
          onChange={(newValue) => debouncedOnChange(newValue || "")}
          placeholder={placeholder}
          plugins={[
            headingsPlugin(),
            listsPlugin(),
            thematicBreakPlugin(),
            quotePlugin(),
            linkPlugin(),
            linkDialogPlugin(),
            toolbarPlugin({
              toolbarContents: () => (
                <>
                  <BlockTypeSelect />
                  <BoldItalicUnderlineToggles />
                  <CreateLink />
                  <ListsToggle options={["bullet", "number"]} />
                  <InsertThematicBreak />
                </>
              ),
            }),
          ]}
        />
      </Box>
    );
  };
